// Push Notifications Onboarding Modal - Play Store Compliant
// Controlled by App.js - displays when user is logged in
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bell, BellRing, Shield, Clock, Trophy, X } from 'lucide-react';

export default function NotificationsOnboarding({ onComplete, onSkip }) {
  const handleActivate = () => {
    // Mark as seen
    localStorage.setItem('notifications_onboarding_seen', 'true');
    // Call onComplete which will trigger permission request
    onComplete?.();
  };

  const handleSkip = () => {
    // Mark as seen but don't request permission
    localStorage.setItem('notifications_onboarding_seen', 'true');
    localStorage.setItem('notifications_skipped', 'true');
    onSkip?.();
  };

  const handleLater = () => {
    // Don't mark as seen - will show again next time
    onSkip?.();
  };

  const benefits = [
    {
      icon: Clock,
      title: "Rappels personnalisés",
      description: "Pesée quotidienne, repas, entraînements"
    },
    {
      icon: Trophy,
      title: "Célébrez vos succès",
      description: "Soyez notifié de vos badges et progrès"
    },
    {
      icon: Shield,
      title: "Contrôle total",
      description: "Désactivez à tout moment dans les paramètres"
    }
  ];

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-sm p-0 overflow-hidden border-0 rounded-2xl" hideClose>
        {/* Header avec gradient */}
        <div className="relative bg-gradient-to-br from-primary via-primary to-secondary p-6 pb-12 text-white">
          <button 
            onClick={handleLater}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="notifications-later-btn"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                <BellRing className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-green-900">!</span>
              </div>
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-center">
            Restez motivé avec les notifications
          </h2>
          <p className="text-sm text-center text-white/80 mt-2">
            Ne manquez plus jamais un rappel important
          </p>
        </div>

        {/* Contenu */}
        <div className="p-6 -mt-6 bg-background rounded-t-3xl">
          {/* Avantages */}
          <div className="space-y-4 mb-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">{benefit.title}</p>
                  <p className="text-xs text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Message de confidentialité */}
          <div className="bg-muted/50 rounded-lg p-3 mb-6">
            <p className="text-xs text-center text-muted-foreground">
              🔒 <strong>Pas de spam</strong> - Uniquement des rappels utiles.
              <br />Vous gardez le contrôle total de vos préférences.
            </p>
          </div>

          {/* Boutons */}
          <div className="space-y-3">
            <Button 
              onClick={handleActivate}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              data-testid="notifications-activate-btn"
            >
              <Bell className="w-5 h-5 mr-2" />
              Activer les notifications
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleSkip}
              className="w-full text-muted-foreground hover:text-foreground"
              data-testid="notifications-skip-btn"
            >
              Non merci, peut-être plus tard
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
